import React, { useMemo, useState } from "react"
import { useGetMedicalInfo, useMedicalInfo } from "../../context/MedicalInfoContext"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { defineMessage, FormattedMessage, useIntl } from "react-intl"
import type { MessageDescriptor } from "react-intl"
import { Form, FormAlert } from "@carrotfertility/carotene-core-x"
import { Box, Divider, Typography } from "@carrotfertility/carotene-core"
import {
  ButtonFooterLayout,
  ContinueButton,
  RoutingFlowTitle,
  SkipButton,
  StepLayout,
  useConvertTranslatedLabel
} from "#/components/carrot-plans/shared"
import { FormRadioButtonGroup } from "#/services/common-forms"
import type { MedicalInfo } from "#/types/medicalInfo"
import { HttpStatusCodes } from "#/utils/HttpStatusCodes"
import type ResponseError from "#/types/responseError"

const MENTAL_HEALTH_SCREENER_SKIP_FIELD = "mentalHealthScreener"

const MENTAL_HEALTH_SCREENER_ROWS = [
  {
    field: "mentalHealthInterest",
    label: defineMessage({ defaultMessage: "Little interest or pleasure in doing things" })
  },
  {
    field: "mentalHealthMood",
    label: defineMessage({ defaultMessage: "Feeling down, depressed, or hopeless" })
  },
  {
    field: "mentalHealthNervous",
    label: defineMessage({ defaultMessage: "Feeling nervous, anxious, or on edge" })
  },
  {
    field: "mentalHealthWorry",
    label: defineMessage({ defaultMessage: "Not being able to stop or control worrying" })
  }
] as const

type MentalHealthScreenerRow = typeof MENTAL_HEALTH_SCREENER_ROWS[number]

type MentalHealthScreenerFormValues = {
  [K in MentalHealthScreenerRow["field"]]: string
}

const MENTAL_HEALTH_SCREENER_OPTIONS: { label: MessageDescriptor; value: string }[] = [
  { label: defineMessage({ defaultMessage: "Not at all" }), value: "NOT_AT_ALL" },
  { label: defineMessage({ defaultMessage: "Several days" }), value: "SEVERAL_DAYS" },
  {
    label: defineMessage({ defaultMessage: "More than half the days" }),
    value: "MORE_THAN_HALF_THE_DAYS"
  },
  { label: defineMessage({ defaultMessage: "Nearly every day" }), value: "NEARLY_EVERY_DAY" }
]

function buildDefaultValues(medicalInfo: MedicalInfo | undefined): MentalHealthScreenerFormValues {
  return MENTAL_HEALTH_SCREENER_ROWS.reduce((acc, row) => {
    acc[row.field] = (medicalInfo?.[row.field] as string | null | undefined) ?? ""
    return acc
  }, {} as MentalHealthScreenerFormValues)
}

function countAnsweredQuestions(formValues: MentalHealthScreenerFormValues): number {
  return MENTAL_HEALTH_SCREENER_ROWS.filter((row) => {
    const value = formValues[row.field]
    return typeof value === "string" && value.length > 0
  }).length
}

export default function MentalHealthScreenerStep(): JSX.Element {
  const intl = useIntl()
  const { updateMedicalInfo, hasQuestionBeenSkipped } = useMedicalInfo()
  const { data: medicalInfo } = useGetMedicalInfo()

  const hasExistingAnswer = MENTAL_HEALTH_SCREENER_ROWS.every((row) => Boolean(medicalInfo?.[row.field]))
  const wasSkipped = hasQuestionBeenSkipped(MENTAL_HEALTH_SCREENER_SKIP_FIELD)

  const { send } = useStateMachine(hasExistingAnswer || wasSkipped ? "" : null)

  const defaultValues = buildDefaultValues(medicalInfo)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const radioOptions = useConvertTranslatedLabel(MENTAL_HEALTH_SCREENER_OPTIONS)

  const validateQuestion = useMemo(
    () =>
      (value: string, formValues: MentalHealthScreenerFormValues): true | string => {
        const answered = countAnsweredQuestions(formValues)
        if (answered === 0 || answered === MENTAL_HEALTH_SCREENER_ROWS.length) {
          return true
        }
        if (!value) {
          return intl.formatMessage({ defaultMessage: "Please select a response" })
        }
        return true
      },
    [intl]
  )

  async function onClickContinue(formValues: MentalHealthScreenerFormValues): Promise<void> {
    setSubmitError(null)
    if (countAnsweredQuestions(formValues) === 0) {
      await onSkip()
      return
    }
    try {
      await updateMedicalInfo(formValues as MedicalInfo)
      send("")
    } catch (error) {
      // API returns 400 unless all four answers are sent or the step is skipped.
      const err = error as ResponseError & { response?: { status?: number } }
      const statusCode = err?.statusCode ?? err?.response?.status
      if (statusCode === HttpStatusCodes.BAD_REQUEST) {
        setSubmitError(
          intl.formatMessage({
            defaultMessage: "Please answer all four questions or skip this step."
          })
        )
        return
      }
      throw error
    }
  }

  async function onSkip(): Promise<void> {
    setSubmitError(null)
    await updateMedicalInfo({ skipFields: [MENTAL_HEALTH_SCREENER_SKIP_FIELD] })
    send("")
  }

  const heading = intl.formatMessage({
    defaultMessage: "Over the last two weeks, how often have you been bothered by the following problems?"
  })

  const disclaimer = intl.formatMessage({
    defaultMessage:
      "This information helps us provide guidance and suggest relevant resources, and is never shared with your benefit sponsor."
  })

  return (
    <>
      <RoutingFlowTitle title={heading} />
      <StepLayout
        header={<span id="mental-health-screener-heading">{heading}</span>}
        description={
          <Typography
            component="span"
            variant="body2"
            sx={(theme) => ({
              display: "block",
              color: theme.palette.text.secondary
            })}
          >
            {disclaimer}
          </Typography>
        }
        formElements={
          <Form<MentalHealthScreenerFormValues> onSubmit={onClickContinue} defaultValues={defaultValues}>
            {submitError ? (
              <FormAlert severity="error" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
                {submitError}
              </FormAlert>
            ) : null}
            <Box sx={() => ({ display: "flex", flexDirection: "column", gap: 0 })}>
              {MENTAL_HEALTH_SCREENER_ROWS.map((row, index) => (
                <Box key={row.field}>
                  <Divider
                    sx={(theme) => ({
                      marginBlockStart: index === 0 ? theme.tokens.spacing.xs : theme.tokens.spacing.lg,
                      marginBlockEnd: theme.tokens.spacing.lg
                    })}
                  />
                  <Typography
                    component="label"
                    id={`${row.field}-label`}
                    variant="body1"
                    sx={() => ({
                      display: "block",
                      margin: 0
                    })}
                  >
                    {intl.formatMessage(row.label)}
                  </Typography>
                  <Box
                    sx={(theme) => ({
                      "& [role='radiogroup']": {
                        display: "grid",
                        gridTemplateColumns: "auto auto",
                        alignItems: "flex-start",
                        gap: 0,
                        inlineSize: "fit-content",
                        [theme.breakpoints.down("md")]: {
                          gridTemplateColumns: "1fr",
                          inlineSize: "100%"
                        }
                      },
                      "& [role='radiogroup'] .MuiFormControlLabel-root": {
                        [theme.breakpoints.down("md")]: {
                          inlineSize: "100%",
                          maxInlineSize: "100%",
                          boxSizing: "border-box"
                        }
                      }
                    })}
                  >
                    <FormRadioButtonGroup
                      name={row.field}
                      options={radioOptions}
                      aria-labelledby={`${row.field}-label`}
                      controllerProps={{
                        rules: {
                          validate: validateQuestion
                        }
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
              skipButton={<SkipButton onClick={onSkip} />}
            />
          </Form>
        }
      />
    </>
  )
}
